import React, { useState,  useEffect } from "react";
import { Link as DomLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  AppBar as MuiAppbar,
  Stack,
  Toolbar,
  Container,
  IconButton,
  Popover,
  MenuItem,
  Box,
  Typography,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import LanguageIcon from "@mui/icons-material/Language";
import { useMyContext } from "../../MyContext";
import { useTranslation } from "react-i18next";
import {
  Link as ScrollLink,
  animateScroll as scroll,
  scroller,
} from "react-scroll";
import DrawerComp from "./DrawerComp";
import SocialIcons from "./SocialIcons";
import {
  getLocalizedPath,
  stripLanguageFromPathname,
} from "../../locales/routing";


const HeaderStyling = styled(MuiAppbar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontFamily: theme.typography.h1.fontFamily,
  zIndex: 996,
  position: "sticky",
  top: 0,
  minWidth: "100%",
  minHeight: "100%",
}));

const Logo = styled(Typography)(({ theme }) => ({
  marginBottom: "-20px",
  marginTop: "-5px",
  fontWeight: 700,
  display: "flex",
  fontFamily: theme.typography.h1.fontFamily,
  fontSize: theme.typography.h1.fontSize,
  color: theme.palette.text.light,
  textTransform: "uppercase",
  cursor: "pointer",
  textDecoration: "none",
}));

const AboutButton = styled(ScrollLink)(({ theme, lang }) => ({
  ...theme.components.MuiButton.styleOverrides.heroButton,
  fontSize: lang === "en" ? "17px" : "13px",
  padding: 0,
  color: theme.palette.text.light,
  fontFamily: theme.typography.allVariants.fontFamily,
  fontWeight: 700,
  cursor: "pointer",
  display: "flex",
  "&:hover": {
    fontSize: "22px",
    fontWeight: 900,
  },
}));

const LanguageSwitcher = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.light,
  display: "flex",
  alignItems: "center",
}));

const LanguagePopover = styled(Popover)(({ theme }) => ({
  width: "150px",
  fontFamily: theme.typography.fontFamily,
}));

const AnimatedDrawer = styled(Drawer)(({ theme }) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.enter,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

export default function AppBar({ headerRef, menuOnly }) {
  const drawerWidth = "63%";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerToggle, setDrawerToggle] = useState(false);
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const { rest, setLang, setOnlyMenu } = useMyContext();
  const { i18n, t } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();

  const navigateToLocalized = (pathname) => {
    navigate(getLocalizedPath(lang, pathname));
  };

  const handleDrawerToggle = () => {
    setDrawerToggle((prevState) => !prevState);
  };

  const handleLanguageClick = (event) => {
    event.preventDefault();
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageSelect = (selectedLanguage) => {
    setLang(selectedLanguage);
    i18n.changeLanguage(selectedLanguage);
    const currentPath = stripLanguageFromPathname(location.pathname);
    const localizedPath = `${getLocalizedPath(
      selectedLanguage,
      currentPath
    )}${location.search}`;
    navigate(localizedPath, { replace: true });
    handleLanguageClose();
  };

  const scrollToTopFunc = () => {
    scroll.scrollToTop({
      smooth: true,
    });
  };

  const [showContactBar, setShowContactBar] = useState(true);
  useEffect(() => {
  const handleScroll = () => {
    const y = window.scrollY;

    setShowContactBar((prev) => {
      if (y > 370 && prev) return false; // hide only if it's currently shown
      if (y < 260 && !prev) return true; // show only if it's currently hidden
      return prev; // no change
    });
  };

  window.addEventListener("scroll", handleScroll);
  return () => {
    window.removeEventListener("scroll", handleScroll);
  };
  }, []);

  
  return (
    <>
      <HeaderStyling ref={ headerRef } sx={ {
        transition: "all 0.3s ease-in-out",
        transform: !showContactBar
          ? `translateY(${Math.min(0, -100 + scrollY)}px)`
          : "translateY(0)",}} >
        {!isMobile  && (
          <Box
   sx={{
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    overflow: "hidden",
    height: showContactBar ? "50px" : "0px",
    padding: showContactBar ? "5px 30px" : "0px 30px",
    fontSize: "1.5rem",
    transition: "all 0.4s ease-in-out",
  }}
          >
            <Box
              sx={{
                display: "flex",
                // alignSelf: "center",
                alignItems: "center",
              }}
            >
              Contact Us: {rest.tel}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              Follow Us: <SocialIcons />
            </Box>
          </Box>
        )}
        <Container>
          <Toolbar>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Logo
                component="h1"
                onClick={() => {
                  navigateToLocalized("/");
                  scrollToTopFunc();
                }}
              >
                {rest.name}
              </Logo>

              <Stack
                direction={isMobile ? "row" : "row-reverse"}
                spacing={2}
                alignItems="center"
              >
                <LanguageSwitcher color="inherit" onClick={handleLanguageClick}>
                  <LanguageIcon />
                </LanguageSwitcher>
                <LanguagePopover
                  open={Boolean(languageAnchor)}
                  anchorEl={languageAnchor}
                  onClose={handleLanguageClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem onClick={() => handleLanguageSelect("en")}>
                    English
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageSelect("el")}>
                    Ελληνικά
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageSelect("de")}>
                    {" "}
                    Deutsch
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageSelect("se")}>
                    {" "}
                    Srpski
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageSelect("ua")}>
                    {" "}
                    Українська
                  </MenuItem>
                  <MenuItem onClick={() => handleLanguageSelect("ru")}>
                    Русский
                  </MenuItem>
                </LanguagePopover>
                {!isMobile ? (
                  <>
                    <AboutButton
                      lang={lang}
                      to="career"
                      smooth={true}
                      duration={800}
                      spy={true}
                      offset={-headerRef?.current?.offsetTop}
                      onClick={(e) => {
                        navigateToLocalized("/career");
                      }}
                    >
                      {t("career.title1")}
                    </AboutButton>
                    <AboutButton
                      lang={lang}
                      to="about"
                      smooth={true}
                      duration={800}
                      spy={true}
                      offset={-headerRef?.current?.offsetTop}
                      onClick={(e) => {
                        navigateToLocalized("/about");
                        scroller.scrollTo("about", { smooth: true });
                      }}
                    >
                      {t("menu.About")}
                    </AboutButton>
                    {menuOnly !== "2" && (
                      <AboutButton
                        lang={lang}
                        to="menu"
                        smooth={true}
                        duration={800}
                        spy={true}
                        offset={-headerRef?.current?.offsetTop}
                        onClick={(e) => {
                          setOnlyMenu(1);
                          navigateToLocalized("/");
                        }}
                      >
                        {t("menu.Menu")}
                      </AboutButton>
                    )}
                    <AboutButton
                      lang={lang}
                      to="menu"
                      smooth={true}
                      duration={800}
                      spy={true}
                      offset={-headerRef?.current?.offsetTop}
                      onClick={(e) => {
                        navigateToLocalized("/");
                        setOnlyMenu("2");
                        scroller.scrollTo("menu", { smooth: true });
                      }}
                    >
                      Beach Menu
                    </AboutButton>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      color: "white",
                    }}
                  >
                    <IconButton
                      color="inherit"
                      aria-label="open drawer"
                      edge="start"
                      onClick={handleDrawerToggle}
                      sx={{ zIndex: 1300 }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Box>
                )}
              </Stack>
            </Stack>

            <nav>
              <AnimatedDrawer
                open={drawerToggle}
                anchor="right"
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
                sx={{
                  "& .MuiDrawer-paper": {
                    boxSizing: "border-box",
                    width: drawerWidth,
                    backgroundColor: "primary.red",
                  },
                }}
              >
                <DrawerComp
                  handleLanguageSelect={handleLanguageSelect}
                  headerRef={headerRef}
                  setDrawerToggle={setDrawerToggle}
                  drawerToggle={drawerToggle}
                  handleDrawerToggle={handleDrawerToggle}
                  menuOnly={menuOnly}
                />
              </AnimatedDrawer>
              {drawerToggle && (
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "secondary.red",
                    zIndex: 1000,
                  }}
                  onClick={handleDrawerToggle}
                />
              )}{" "}
            </nav>
          </Toolbar>
        </Container>
      </HeaderStyling>
    </>
  );
}
